import { combineReducers, configureStore } from '@reduxjs/toolkit';
import {
  createMigrate,
  FLUSH,
  PAUSE,
  PERSIST,
  persistReducer,
  persistStore,
  PURGE,
  REGISTER
} from 'redux-persist';
import storage from 'redux-persist/lib/storage';

import { adminSessionReducer } from '@/entities/admin-session';
import { classifiersReducer } from '@/entities/classifiers';
import { listPreferencesReducer } from '@/entities/list-preferences';
import { profileToEditReducer, userReducer } from '@/entities/user';
import {
  videoReadingDraftSettingsReducer,
  videoReadingReducer,
  videoReadingSettingsReducer
} from '@/entities/video-reading';

import {
  actorRegistrationReducer,
  directorRegistrationReducer
} from '@/features/registration';

import { migrations } from './migrations';

const persistConfig = {
  key: 'root',
  storage,
  version: 5,
  whitelist: [
    'user',
    'adminSession',
    'videoReading',
    'videoReadingSettings',
    'listPreferences'
  ],
  migrate: createMigrate(migrations, { debug: true })
};

const rootReducer = combineReducers({
  user: userReducer,
  adminSession: adminSessionReducer,
  classifiers: classifiersReducer,
  profileToEdit: profileToEditReducer,
  actorRegistration: actorRegistrationReducer,
  directorRegistration: directorRegistrationReducer,
  videoReading: videoReadingReducer,
  videoReadingSettings: videoReadingSettingsReducer,
  videoReadingDraftSettings: videoReadingDraftSettingsReducer,
  listPreferences: listPreferencesReducer
});

const persistedReducer = persistReducer(persistConfig, rootReducer);

// При использовании Redux-Persist, необходимо специально игнорировать все типы действий, которые он отправляет
// https://redux-toolkit.js.org/usage/usage-guide#use-with-redux-persist
const ignoredActions = [FLUSH, PAUSE, PERSIST, PURGE, REGISTER];

export const setupStore = configureStore({
  reducer: persistedReducer,
  /**
   *
   * @param getDefaultMiddleware middleware по умолчанию
   */
  middleware: getDefaultMiddleware =>
    getDefaultMiddleware({ serializableCheck: { ignoredActions } }).prepend()
});

export const persistor = persistStore(setupStore);

export type State = ReturnType<typeof rootReducer>;
export type RootState = ReturnType<typeof setupStore.getState>;
export type AppDispatch = typeof setupStore.dispatch;
